/**
 * Button - Primary interactive element used throughout the application.
 * Supports multiple visual variants (primary, secondary, quiet, danger, danger-outline, ghost) and sizes (sm, md, lg).
 * Includes built-in loading state with spinner integration.
 * All buttons enforce minimum touch target sizes per WCAG 2.5.5 mobile accessibility guidelines.
 *
 * Used in forms, dialogs, toolbars, and any action-triggering UI.
 *
 * @example
 * <Button variant="primary" size="md" onClick={handleSave}>Save Work Order</Button>
 * <Button variant="danger" isLoading>Deleting...</Button>
 */
import React from 'react';
import { Spinner } from '../Spinner';

// --- Type definitions ---

/** Available visual styles for the button */
export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'danger' | 'danger-outline' | 'ghost';

/** Available size presets — all enforce accessible minimum touch targets */
export type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * Props for the Button component.
 * Extends native HTML button attributes so callers can pass onClick, type, aria-*, etc.
 */
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	/** Visual style variant — defaults to 'primary' */
	variant?: ButtonVariant;
	/** Size preset controlling padding, font size, and min dimensions — defaults to 'md' */
	size?: ButtonSize;
	/** When true, shows a spinner and disables interaction to indicate async work */
	isLoading?: boolean;
	/** When true, button stretches to fill its container width */
	fullWidth?: boolean;
	/** Button label / content */
	children: React.ReactNode;
}

// --- Variant color mappings ---
// Each variant maps to brand CSS variables defined in the Tailwind config / brand.css.
// 'primary' uses the main brand color; 'secondary' is outlined style;
// 'quiet' is the neutral outline used for low-emphasis utilities;
// 'danger' uses red for destructive actions; 'danger-outline' is its outlined
// form, for a destructive action repeated down a list (Deactivate on every
// Admin row), where a solid red block per row would shout; 'ghost' is
// minimal/transparent.
//
// The three-step ladder primary -> secondary -> quiet is what lets a screen show
// several actions at once without them all shouting: exactly one solid brand
// CTA, brand-outlined supporting actions, and neutral-outlined utilities like
// "Generate PDF" that should recede. Each detail page was previously hand-
// rolling the quiet style inline; it belongs on the atom.
const variantClasses: Record<ButtonVariant, string> = {
	primary: 'bg-brand-primary text-white hover:bg-brand-primary-dark focus:ring-brand-primary',
	secondary: 'bg-white text-brand-primary border border-brand-primary hover:bg-brand-primary/10 focus:ring-brand-primary',
	quiet: 'bg-white text-brand-text-muted border border-brand-border hover:bg-brand-surface focus:ring-brand-primary',
	danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
	'danger-outline': 'bg-white text-red-600 border border-red-300 hover:bg-red-50 focus:ring-red-500',
	ghost: 'bg-transparent text-gray-700 hover:bg-gray-100 focus:ring-gray-400',
};

// --- Size mappings ---
// min-w and min-h values ensure all sizes meet the 44px minimum touch target.
// 'sm' keeps its 36px height only from `desktop` up, where the pointer is a
// mouse and dense toolbars are the point. Below that — phones and tablets, i.e.
// every touch device — it grows to the same 44px floor as the other sizes, so a
// dense-UI choice made for desktop can't quietly ship an undersized tap target
// to the field team (Save/Add/Change Password were all landing at 36px).
const sizeClasses: Record<ButtonSize, string> = {
	sm: 'px-12px py-6px text-14px min-w-44px min-h-44px desktop:min-h-36px',
	md: 'px-16px py-10px text-16px min-w-44px min-h-44px',
	lg: 'px-24px py-12px text-18px min-w-44px min-h-48px',
};

const Button: React.FC<ButtonProps> = ({ variant = 'primary', size = 'md', isLoading = false, fullWidth = false, disabled, className = '', children, ...rest }) => {
	// Base classes shared by all buttons:
	// - inline-flex centering for icon + text alignment
	// - focus:ring for keyboard navigation visibility (accessibility)
	// - disabled styles to visually communicate non-interactive state
	const baseClasses = 'inline-flex items-center justify-center font-sans font-medium rounded-6px transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

	return (
		// type="button" prevents accidental form submission when used outside forms.
		// Disabled when loading to prevent duplicate submissions.
		<button type="button" disabled={disabled || isLoading} className={`button button--${variant}${isLoading ? ' button--loading' : ''} ${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${fullWidth ? 'w-full' : ''} ${className}`} {...rest} data-ref="atom-button">
			{/* Show spinner to the left of the label during loading state */}
			{isLoading && <Spinner size="sm" className="button__spinner mr-8px" />}
			{children}
		</button>
	);
};

export default Button;
